import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../api/client';

export type FenceTraceSnapshot = {
  status: 'available' | 'unavailable';
  reason?: string | null;
  checked_at?: string;
  stale?: boolean;
  truncated?: boolean;
  calls: {
    provider_call_id: string;
    correlation_id: string | null;
    request_id: string | null;
    request_id_status?: 'withheld' | 'unavailable';
    session_id: string | null;
    http_status: number | null;
    cache_read_tokens: number | null;
    cache_write_tokens: number | null;
    budget_count_unit: string | null;
    budget_outcome: string | null;
  }[];
};

export function FenceTraceEvidence({ snapshot }: { snapshot: FenceTraceSnapshot | null }) {
  return (
    <>
      <p>{snapshot?.status === 'available' ? 'Fence evidence available' : 'Fence evidence unavailable'}</p>
      {snapshot?.checked_at && <p>Last checked: {snapshot.checked_at}</p>}
      {snapshot?.stale && <p>Showing previously captured evidence; refresh did not return current evidence.</p>}
      {snapshot?.truncated && <p>Partial history (at most 50 calls); totals are not complete.</p>}
      {snapshot?.calls.map(call => (
        <dl key={call.provider_call_id}>
          <dt>Fence provider call</dt><dd>{call.provider_call_id}</dd>
          <dt>Fence correlation</dt><dd>{call.correlation_id ?? 'Unavailable'}</dd>
          <dt>Caller request ID</dt><dd>{call.request_id_status === 'withheld' || call.request_id != null ? 'Withheld (untrusted)' : 'Unavailable'}</dd>
          <dt>Session</dt><dd>{call.session_id ?? 'Unavailable'}</dd>
          <dt>HTTP status</dt><dd>{call.http_status ?? 'Unavailable'}</dd>
          <dt>Cache read tokens</dt><dd>{call.cache_read_tokens ?? 'Unavailable'}</dd>
          <dt>Cache write tokens</dt><dd>{call.cache_write_tokens ?? 'Unavailable'}</dd>
          <dt>Budget accounting unit</dt><dd>{call.budget_count_unit ?? 'Unavailable'}</dd>
          <dt>Budget outcome</dt><dd>{call.budget_outcome ?? 'Unavailable'}</dd>
        </dl>
      ))}
    </>
  );
}

// Parent keys by conversation/run so neither prior data nor in-flight requests cross selection.
export function ConversationFenceTrace({ apiClient, conversationId, runId }: {
  apiClient: ApiClient; conversationId: string; runId: string;
}) {
  const [snapshot, setSnapshot] = useState<FenceTraceSnapshot | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const epoch = useRef(0);
  const path = `/api/conversations/${encodeURIComponent(conversationId)}/runs/${encodeURIComponent(runId)}/fence-trace`;
  useEffect(() => {
    const request = ++epoch.current;
    setSnapshot(null);
    setBusy(true);
    setFailed(false);
    void apiClient.get<FenceTraceSnapshot>(path).then(value => {
      if (request === epoch.current) setSnapshot(value);
    }).catch(() => {
      if (request === epoch.current) setFailed(true);
    }).finally(() => {
      if (request === epoch.current) setBusy(false);
    });
    return () => { epoch.current++; };
  }, [apiClient, path]);

  async function refresh() {
    const request = ++epoch.current;
    setBusy(true);
    setFailed(false);
    try {
      const value = await apiClient.post<FenceTraceSnapshot>(`${path}/refresh`);
      if (request === epoch.current) setSnapshot(value);
    } catch {
      if (request === epoch.current) setFailed(true);
    } finally {
      if (request === epoch.current) setBusy(false);
    }
  }
  return (
    <details className="runner-sidebar__meta">
      <summary>AI Fence trace</summary>
      {failed ? <p>Trace read unavailable. No current evidence can be confirmed.</p> : <FenceTraceEvidence snapshot={snapshot} />}
      <p>Trace collection is explicit only; terminal completion never waits for Fence. History can lag. Refresh is terminal-run only and limited to once per 30 seconds. Missing values are not zero or budget approval.</p>
      <button type="button" className="ghost" disabled={busy} onClick={() => void refresh()}>
        {busy ? 'Reading…' : 'Refresh Fence history'}
      </button>
    </details>
  );
}
